import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { api } from "../api";

// The question: what did the lab's projects cost before the lab, and what will moving them save?

type Plan = "move" | "retire" | "stays" | "review";

interface CostRow {
  slug: string;
  name: string;
  client: string;
  stage: string;
  plan: Plan;
  months: { month: string; usd: number }[];
  totalUsd: number;
  perMonthUsd: number;
}

interface Costs {
  months: string[];
  /** Months measured whole; the averages and totals use only these. */
  wholeMonths: string[];
  /** The month still running, measured up to `through`. */
  partial: { month: string; through: string; projectsUsd: number; accountUsd: number | null } | null;
  basis: string;
  labMonthlyEstimateUsd: number;
  account: { month: string; usd: number; through: string | null }[];
  projects: CostRow[];
  totals: {
    totalUsd: number;
    perMonthUsd: number;
    lastMonth: string | null;
    lastMonthUsd: number;
    shareOfAccount: number | null;
    movablePerMonthUsd: number;
    estimatedSavingPerMonthUsd: number;
  };
}

const PLAN: Record<Plan, { label: string; tone: string }> = {
  move: { label: "Move to the lab", tone: "var(--accent)" },
  retire: { label: "Retire", tone: "#2f70bb" },
  review: { label: "Review", tone: "#a86f0c" },
  stays: { label: "Stays", tone: "var(--control)" },
};
const PLAN_ORDER: Plan[] = ["move", "retire", "review", "stays"];

const usd = (n: number) => `$${Math.round(n).toLocaleString("en-US")}`;
const monthName = (iso: string) => new Date(iso).toLocaleString("en-US", { month: "short", timeZone: "UTC" });
/** "Nov 1–4" for the month still running, "Nov" for a whole one. */
const monthLabel = (iso: string, partial: Costs["partial"]) =>
  partial?.month === iso ? `${monthName(iso)} 1–${new Date(partial.through).getUTCDate()}` : monthName(iso);

/** Stacked columns per month, one segment per plan, drawn to one scale. */
function MonthlyChart({ data }: { data: Costs }) {
  const W = 640, H = 240, top = 22, bottom = 26, left = 56, right = 8;
  const stacks = data.months.map((m) => {
    let y0 = 0;
    const parts = PLAN_ORDER.map((plan) => {
      const v = data.projects.filter((p) => p.plan === plan).reduce((n, p) => n + (p.months.find((x) => x.month === m)?.usd ?? 0), 0);
      const seg = { plan, y0, y1: y0 + v, v };
      y0 += v;
      return seg;
    });
    return { month: m, parts, total: y0 };
  });
  const max = Math.max(...stacks.map((s) => s.total), 1);
  const step = max > 1200 ? 500 : max > 600 ? 250 : 100;
  const top_ = Math.ceil((max * 1.08) / step) * step;
  const y = (v: number) => top + (H - top - bottom) * (1 - v / top_);
  const ticks = Array.from({ length: top_ / step + 1 }, (_, i) => i * step);
  const band = (W - left - right) / stacks.length;
  const bw = Math.min(72, band * 0.5);
  const [hover, setHover] = useState<string | null>(null);

  return (
    <div>
      <div className="row-legend" style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 8 }}>
        {PLAN_ORDER.map((p) => (
          <span key={p} className="small muted" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: PLAN[p].tone }} />
            {PLAN[p].label}
          </span>
        ))}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Monthly cost of lab projects, stacked by plan">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={left} x2={W - right} y1={y(t)} y2={y(t)} stroke="#e5e5e5" />
            <text x={left - 8} y={y(t)} dy="0.32em" textAnchor="end" fontSize="11" fill="var(--muted)">{usd(t)}</text>
          </g>
        ))}
        {stacks.map((s, i) => {
          const x = left + band * i + (band - bw) / 2;
          const running = data.partial?.month === s.month;
          return (
            <g key={s.month} onMouseEnter={() => setHover(s.month)} onMouseLeave={() => setHover(null)}>
              {s.parts.filter((p) => p.v > 0).map((p) => (
                <rect key={p.plan} x={x} width={bw} y={y(p.y1)} height={Math.max(0, y(p.y0) - y(p.y1) - 1)} fill={PLAN[p.plan].tone} opacity={running ? 0.5 : 1} rx="2">
                  <title>{`${monthLabel(s.month, data.partial)}${running ? " (so far)" : ""} · ${PLAN[p.plan].label}: ${usd(p.v)}`}</title>
                </rect>
              ))}
              <text x={x + bw / 2} y={y(s.total) - 6} textAnchor="middle" fontSize="12" fill="var(--text)" fontWeight={hover === s.month ? 600 : 400}>{usd(s.total)}</text>
              <text x={x + bw / 2} y={H - 8} textAnchor="middle" fontSize="11" fill="var(--muted)">{monthLabel(s.month, data.partial)}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export function CostsPage() {
  const q = useQuery({ queryKey: ["costs"], queryFn: () => api<Costs>("/costs") });
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 }>({ key: "perMonthUsd", dir: -1 });

  const rows = useMemo(() => {
    const list = [...(q.data?.projects ?? [])];
    const val = (r: CostRow): number | string => {
      if (sort.key === "name") return r.name;
      if (sort.key === "plan") return PLAN_ORDER.indexOf(r.plan);
      if (sort.key.startsWith("m:")) return r.months.find((m) => m.month === sort.key.slice(2))?.usd ?? 0;
      return r[sort.key as "perMonthUsd" | "totalUsd"];
    };
    return list.sort((a, b) => {
      const va = val(a), vb = val(b);
      return (typeof va === "number" && typeof vb === "number" ? va - vb : String(va).localeCompare(String(vb))) * sort.dir;
    });
  }, [q.data, sort]);

  const th = (key: string, label: string, num = false) => (
    <th className={num ? "num" : undefined} aria-sort={sort.key === key ? (sort.dir === 1 ? "ascending" : "descending") : undefined}>
      <button
        className="btn ghost"
        style={{ height: 24, padding: "0 4px", fontWeight: sort.key === key ? 600 : 400 }}
        onClick={() => setSort((s) => ({ key, dir: s.key === key ? (s.dir === 1 ? -1 : 1) : num ? -1 : 1 }))}
      >
        {label}
      </button>
    </th>
  );

  if (q.isPending) {
    return (
      <div className="canvas" aria-busy="true">
        <div className="title-row"><div><h1>Costs</h1><p className="sub"><span className="skeleton" style={{ width: 480, height: 16 }} /></p></div></div>
        <div className="kpis">{["Lab projects", "Per month", "Move or retire", "Estimated saving"].map((l) => (
          <div className="kpi" key={l}><div className="kpi-label">{l}</div><div className="kpi-value"><span className="skeleton" style={{ width: 80, height: 28 }} /></div></div>
        ))}</div>
      </div>
    );
  }
  if (q.error) {
    return (
      <div className="canvas">
        <div className="title-row"><h1>Costs</h1></div>
        <div className="panel">Couldn't load the cost baseline: {q.error.message}. <button className="btn ghost" onClick={() => q.refetch()}>Retry</button></div>
      </div>
    );
  }
  const d = q.data;
  const t = d.totals;
  const whole = d.wholeMonths;
  const n = whole.length;
  const period = `${monthName(whole[0] ?? "")} to ${monthName(whole[n - 1] ?? "")} ${new Date(whole[n - 1] ?? "").getUTCFullYear()}`;
  const accountTotal = d.account.filter((a) => a.through === null).reduce((n, a) => n + a.usd, 0);
  const p = d.partial;

  return (
    <div className="canvas">
      <div className="title-row">
        <div>
          <h1>Costs</h1>
          <p className="sub">
            Lab projects cost {usd(t.perMonthUsd)} a month ({((t.shareOfAccount ?? 0) * 100).toFixed(1)}% of the account) from {period}.
            Moving the dev boxes and retiring the stopped pilots saves an estimated {usd(t.estimatedSavingPerMonthUsd)} a month.
            {p && ` ${monthLabel(p.month, p)} so far: ${usd(p.projectsUsd)} for the lab projects${p.accountUsd === null ? "" : `, ${usd(p.accountUsd)} for the whole account`} (AWS is still adding the last day).`}
          </p>
        </div>
      </div>

      <div className="kpis">
        <div className="kpi"><div className="kpi-label">Lab projects, {n} months</div><div className="kpi-value num">{usd(t.totalUsd)}</div><div className="kpi-note">{period}</div></div>
        <div className="kpi"><div className="kpi-label">Per month</div><div className="kpi-value num">{usd(t.perMonthUsd)}</div><div className="kpi-note">{t.lastMonth ? `${new Date(t.lastMonth).toLocaleString("en-US", { month: "long", timeZone: "UTC" })} alone ${usd(t.lastMonthUsd)}` : ""}</div></div>
        <div className="kpi"><div className="kpi-label">Move or retire</div><div className="kpi-value num">{usd(t.movablePerMonthUsd)}<span className="unit">/mo</span></div><div className="kpi-note">what those projects cost today</div></div>
        <div className="kpi"><div className="kpi-label">Estimated saving</div><div className="kpi-value num">{usd(t.estimatedSavingPerMonthUsd)}<span className="unit">/mo</span></div><div className="kpi-note">after the lab's own ≈{usd(d.labMonthlyEstimateUsd)}/mo · {usd(t.estimatedSavingPerMonthUsd * 12)} a year</div></div>
      </div>

      <div className="split">
        <section className="panel" aria-labelledby="by-month">
          <div className="section-head"><h2 id="by-month">Per month, by plan</h2><span className="stamp">lab projects only</span></div>
          <MonthlyChart data={d} />
        </section>
        <aside className="panel" aria-labelledby="account">
          <h2 id="account" style={{ marginBottom: 12 }}>The whole account</h2>
          <dl className="kv" style={{ gridTemplateColumns: "minmax(0, 1fr) auto" }}>
            {d.account.map((a) => (
              <><dt key={`m-${a.month}`}>{monthLabel(a.month, p)}{a.through ? " (so far)" : ""}</dt><dd key={`v-${a.month}`} className="num" style={{ textAlign: "right" }}>{usd(a.usd)}</dd></>
            ))}
            <dt><strong>{n} whole months</strong></dt><dd className="num" style={{ textAlign: "right" }}><strong>{usd(accountTotal)}</strong></dd>
          </dl>
          <p className="muted small" style={{ marginTop: 16 }}>
            Most of the account is the IDN / TMS platform in Frankfurt and AWS Support, outside the lab.
          </p>
        </aside>
      </div>

      <section className="panel flush" aria-labelledby="per-project">
        <div className="panel-head"><h2 id="per-project">Per project</h2><span className="stamp num">{rows.length} projects</span></div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {th("name", "Project")}
                {th("plan", "Plan")}
                {d.months.map((m) => <span key={m} style={{ display: "contents" }}>{th(`m:${m}`, monthLabel(m, p), true)}</span>)}
                {th("totalUsd", `${n} months`, true)}
                {th("perMonthUsd", "Per month", true)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.slug}>
                  <td>
                    <Link to={`/p/${r.slug}`} className="cell-main">{r.name}</Link>
                    <div className="cell-sub">{r.client || "—"}</div>
                  </td>
                  <td>
                    <span className="health" style={{ gap: 8 }}>
                      <span className="dot" style={{ background: PLAN[r.plan].tone }} />{PLAN[r.plan].label}
                    </span>
                  </td>
                  {d.months.map((m) => {
                    const v = r.months.find((x) => x.month === m)?.usd;
                    return <td key={m} className="num">{v === undefined ? "—" : usd(v)}</td>;
                  })}
                  <td className="num">{usd(r.totalUsd)}</td>
                  <td className="num"><strong>{usd(r.perMonthUsd)}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <p className="stamp" style={{ maxWidth: "90ch" }}>{d.basis}</p>
    </div>
  );
}
